// src/components/module/ventas/CarritoVenta.tsx
// Rediseño corporativo estilo Notion/Linear
import React, { useState, useContext } from "react";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  TextField,
  Box,
  Typography,
  Tooltip,
  InputAdornment,
  Stack,
  Fade,
  alpha,
} from "@mui/material";
import {
  Delete as DeleteIcon, Add as AddIcon, Remove as RemoveIcon,
  ShoppingCart, Percent as PercentIcon,
} from "@mui/icons-material";
import { formatMoney } from "../../../utils/format";
import { VentasApi } from "../../../services/api/VentasApi";
import Swal from "sweetalert2";
import { toast } from "../../../utils/toast";
import { DarkModeContext } from "../../../context/darkModeContext";

interface ArticuloVenta {
  Id: number; Nombre: string; EAN: string; PrecioVenta: number;
  Cantidad: number; Descuento: number; Valor: number; stock: number;
  Impuesto?: Array<{ Id: number; Nombre: string; Valor: number }>;
}

interface CarritoVentaProps {
  idVenta: number; articulos: ArticuloVenta[];
  onStockUpdated: (response: any) => void;
  showingPayment?: boolean; esCotizacion?: boolean;
}

const P = {
  light: { bg: "#FFFFFF", bgS: "#F7F7F5", bgT: "#F1F1EF", text: "#37352F", textS: "#6B6B6B", textT: "#9B9A97", border: "#E9E9E7", accent: "#2383E2", accentBg: "#E8F0FE", success: "#0F7B6C", successBg: "#DBEDDB", error: "#E03E3E", errorBg: "#FBE4E4", warning: "#D9730D", warningBg: "#FADEC9", hover: "rgba(55,53,47,0.04)" },
  dark: { bg: "#191919", bgS: "#202020", bgT: "#2B2B2B", text: "#E8E8E6", textS: "#9B9A97", textT: "#6B6B6B", border: "#333333", accent: "#529CCA", accentBg: "#143A52", success: "#4DAB9A", successBg: "#1A2F2A", error: "#D44C47", errorBg: "#3D2020", warning: "#C77D48", warningBg: "#2E2215", hover: "rgba(255,255,255,0.04)" },
};

export const CarritoVenta: React.FC<CarritoVentaProps> = ({ idVenta, articulos, onStockUpdated, showingPayment = false, esCotizacion = false }) => {
  const [processingItem, setProcessingItem] = useState<number | null>(null);
  const { darkMode } = useContext(DarkModeContext);
  const p = darkMode ? P.dark : P.light;

  const ajustarStock = async (idArticulo: number, accion: "AddArt" | "DelArt" | "DelAll") => {
    setProcessingItem(idArticulo);
    try { onStockUpdated(await VentasApi.ajustarStock({ idVenta, idArticulo, cantidad: 1, accion })); }
    catch (e) { toast.error((e as Error).message); }
    finally { setProcessingItem(null); }
  };

  const eliminarArticulo = async (a: ArticuloVenta) => {
    const r = await Swal.fire({ title: "¿Eliminar artículo?", text: `"${a.Nombre}" será eliminado`, icon: "warning", showCancelButton: true, confirmButtonColor: p.error, confirmButtonText: "Eliminar", cancelButtonText: "Cancelar", reverseButtons: true });
    if (r.isConfirmed) await ajustarStock(a.Id, "DelAll");
  };

  const calcTotal = (a: ArticuloVenta) => {
    const sub = a.PrecioVenta * a.Cantidad;
    return sub - sub * (a.Descuento / 100);
  };

  const thStyle = { fontSize: "0.72rem", fontWeight: 600, color: p.textT, textTransform: "uppercase" as const, letterSpacing: "0.04em", borderBottom: `1px solid ${p.border}`, py: 1.2, bgcolor: p.bgS };
  const tdStyle = { borderBottom: `1px solid ${p.border}`, py: 1.5, fontSize: "0.84rem" };

  if (articulos.length === 0) return (
    <Box sx={{ p: 5, textAlign: "center", border: `2px dashed ${p.border}`, borderRadius: "10px", bgcolor: p.bgS }}>
        <ShoppingCart sx={{ fontSize: 48, color: p.textT, opacity: 0.3, mb: 1 }} />
        <Typography sx={{ fontSize: "0.9rem", fontWeight: 600, color: p.textS }}>Carrito Vacío</Typography>
        <Typography sx={{ fontSize: "0.78rem", color: p.textT, mt: 0.5 }}>
          {showingPayment ? "No hay artículos en esta venta" : "Busca y agrega artículos"}
        </Typography>
      </Box>
  );

  return (
    <Box>
        {/* Header */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box sx={{ width: 32, height: 32, borderRadius: "8px", bgcolor: p.accentBg, display: "flex", alignItems: "center", justifyContent: "center", color: p.accent, "& svg": { fontSize: 16 } }}>
              <ShoppingCart />
            </Box>
            <Box>
              <Typography sx={{ fontSize: "0.9rem", fontWeight: 650, color: p.text }}>Carrito de Compra</Typography>
              <Typography sx={{ fontSize: "0.72rem", color: p.textT }}>{articulos.length} {articulos.length === 1 ? "artículo" : "artículos"}</Typography>
            </Box>
          </Stack>
          <Box sx={{ px: 1.2, py: 0.3, borderRadius: "6px", bgcolor: p.accentBg, color: p.accent, fontSize: "0.75rem", fontWeight: 600 }}>{articulos.length} items</Box>
        </Stack>

        {/* Table */}
        <Paper elevation={0} sx={{ borderRadius: "10px", border: `1px solid ${p.border}`, overflow: "hidden" }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={thStyle}>Artículo</TableCell>
                  <TableCell align="center" sx={thStyle}>Precio</TableCell>
                  <TableCell align="center" sx={thStyle}>Cantidad</TableCell>
                  <TableCell align="center" sx={thStyle}>Dcto.</TableCell>
                  <TableCell align="right" sx={thStyle}>Subtotal</TableCell>
                  {!showingPayment && <TableCell align="center" sx={thStyle}></TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {articulos.map((a, i) => (
                  <Fade in timeout={200 + i * 50} key={a.Id}>
                    <TableRow sx={{ "&:hover": { bgcolor: p.hover }, transition: "background 0.1s" }}>
                      <TableCell sx={tdStyle}>
                        <Stack direction="row" alignItems="center" spacing={1.5}>
                          <Box sx={{ width: 32, height: 32, borderRadius: "7px", bgcolor: p.bgT, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.72rem", fontWeight: 700, color: p.textS, flexShrink: 0 }}>{a.Nombre[0]}</Box>
                          <Box>
                            <Typography sx={{ fontSize: "0.84rem", fontWeight: 500, color: p.text }}>{a.Nombre}</Typography>
                            <Typography sx={{ fontSize: "0.7rem", color: p.textT, fontFamily: "'IBM Plex Mono', monospace" }}>{a.EAN}</Typography>
                          </Box>
                        </Stack>
                      </TableCell>
                      <TableCell align="center" sx={{ ...tdStyle, fontFamily: "'IBM Plex Mono', monospace", fontWeight: 600, color: p.success }}>{formatMoney(a.PrecioVenta)}</TableCell>
                      <TableCell align="center" sx={tdStyle}>
                        {showingPayment ? (
                          <Box sx={{ display: "inline-flex", px: 1, py: 0.2, borderRadius: "5px", bgcolor: p.bgT, fontWeight: 700, fontSize: "0.84rem" }}>{a.Cantidad}</Box>
                        ) : (
                          <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.5}>
                            <IconButton size="small" onClick={() => ajustarStock(a.Id, "DelArt")} disabled={processingItem === a.Id || a.Cantidad <= 1}
                              sx={{ width: 26, height: 26, borderRadius: "6px", bgcolor: p.errorBg, color: p.error, "& svg": { fontSize: 14 }, "&:hover": { bgcolor: alpha(p.error, 0.15) }, "&.Mui-disabled": { opacity: 0.3 } }}>
                              <RemoveIcon />
                            </IconButton>
                            <Typography sx={{ minWidth: 28, textAlign: "center", fontWeight: 700, fontSize: "0.88rem" }}>{a.Cantidad}</Typography>
                            <IconButton size="small" onClick={() => ajustarStock(a.Id, "AddArt")} disabled={processingItem === a.Id || a.Cantidad >= a.stock}
                              sx={{ width: 26, height: 26, borderRadius: "6px", bgcolor: p.successBg, color: p.success, "& svg": { fontSize: 14 }, "&:hover": { bgcolor: alpha(p.success, 0.15) }, "&.Mui-disabled": { opacity: 0.3 } }}>
                              <AddIcon />
                            </IconButton>
                          </Stack>
                        )}
                      </TableCell>
                      <TableCell align="center" sx={tdStyle}>
                        {a.Descuento > 0 ? (
                          <Box sx={{ display: "inline-flex", px: 1, py: 0.2, borderRadius: "5px", bgcolor: p.errorBg, color: p.error, fontSize: "0.78rem", fontWeight: 600 }}>{a.Descuento}%</Box>
                        ) : <Typography sx={{ fontSize: "0.82rem", color: p.textT }}>—</Typography>}
                      </TableCell>
                      <TableCell align="right" sx={tdStyle}>
                        {a.Descuento > 0 && (
                          <Typography sx={{ fontSize: "0.72rem", textDecoration: "line-through", color: p.textT }}>{formatMoney(a.PrecioVenta * a.Cantidad)}</Typography>
                        )}
                        <Typography sx={{ fontSize: "0.88rem", fontWeight: 700, color: p.success, fontFamily: "'IBM Plex Mono', monospace" }}>{formatMoney(calcTotal(a))}</Typography>
                      </TableCell>
                      {!showingPayment && (
                        <TableCell align="center" sx={tdStyle}>
                          <Tooltip title="Eliminar">
                            <IconButton size="small" onClick={() => eliminarArticulo(a)} disabled={processingItem === a.Id}
                              sx={{ width: 28, height: 28, borderRadius: "6px", color: p.error, "&:hover": { bgcolor: p.errorBg } }}>
                              <DeleteIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      )}
                    </TableRow>
                  </Fade>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>
  );
};
